/**
 * TryonScene — Three.js scene setup for the glasses overlay.
 *
 * Uses a single WebGLRenderer shared across the session (expensive to create).
 * Three.js is loaded via dynamic import at runtime — it is NOT in package.json
 * as a production dependency (only as a devDependency for types).
 *
 * Architecture:
 *  - Single WebGLRenderer with opaque canvas (alpha: false)
 *  - Single PerspectiveCamera with ~63° FOV matching typical webcam vertical FOV
 *  - Video feed rendered as a THREE.VideoTexture plane at z=-500 (same canvas)
 *  - PBR-only materials (MeshStandardMaterial) on glasses
 *  - GLB models loaded via GLTFLoader + shared DRACOLoader (singleton)
 *  - KHR_materials_variants for colour/finish switching
 *  - Continuous rendering via requestAnimationFrame (widget loop), NOT on-demand
 */

import type {
  WebGLRenderer,
  Scene,
  PerspectiveCamera,
  Group,
  Mesh,
  Object3D,
  Material,
} from 'three';
import type { GLTF } from 'three/addons/loaders/GLTFLoader.js';
import type { EyewearLandmarks as FaceLandmarks } from '@mirrably/tracking';
import type { ThreeModule, VariantDef, IMaterialMutationScope, ManagedRenderer } from '@mirrably/renderer';
import {
  createRenderer,
  createVideoPlane,
  createPMREMEnvironment,
  disposeMeshTree,
  parseVariants,
  upgradeAcetateMaterials,
  EYEWEAR_CAMERA_FOV,
  VIDEO_PLANE_DEPTH,
  LM_TO_SCENE_SCALE,
} from '@mirrably/renderer';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Eyewear-specific extension of ThreeModule — adds Euler for glasses rotation. */
interface EyewearThreeModule extends ThreeModule {
  Euler: typeof import('three').Euler;
  MeshStandardMaterial: typeof import('three').MeshStandardMaterial;
  TorusGeometry: typeof import('three').TorusGeometry;
  BoxGeometry: typeof import('three').BoxGeometry;
}

/** Internal render state for the Three.js runtime. */
interface RenderState {
  THREE: EyewearThreeModule;
  canvas: HTMLCanvasElement;
  renderer: WebGLRenderer;
  managedRenderer: ManagedRenderer;
  scene: Scene;
  camera: PerspectiveCamera;
  /** Root group that moves with the face. */
  glassesGroup: Group;
  /** Camera-aligned plane showing the video feed (VideoTexture). */
  videoPlane: Mesh | null;
  /** Currently loaded GLTF data (null when no model loaded). */
  gltf: GLTF | null;
  /** Parsed variant definitions from KHR_materials_variants. */
  variants: VariantDef[];
  /** Active variant name (null = use authored materials). */
  activeVariant: string | null;
  /** Contact shadow disc below the nose bridge (null when no model loaded). */
  contactShadow: Mesh | null;
  /** Last face landmarks applied to the renderer, used by diagnostic fit gates. */
  lastLandmarks: FaceLandmarks | null;
}

type RenderedFrameAxisPx = NonNullable<RenderSnapshot['fit']>['visualFrameAxisPx'];

export interface RenderSnapshot {
  telemetryVersion: 'v1';
  initialized: boolean;
  modelMeshCount: number;
  childCount: number;
  glassesVisible: boolean;
  modelPivotMode: 'none' | 'noseBridge' | 'boundsHeuristic';
  projectedBridgePx: { x: number; y: number };
  canvas: { width: number; height: number };
  roi: { x: number; y: number; width: number; height: number };
  visibleChangedBounds: {
    x: number;
    y: number;
    width: number;
    height: number;
    center: { x: number; y: number };
  } | null;
  visibleChangedPixels: number;
  visibleChangedRatio: number;
  position: { x: number; y: number; z: number };
  scale: number;
  rotation: { x: number; y: number; z: number };
  landmarks: {
    noseBridgePx: { x: number; y: number };
    pupilMidpointPx: { x: number; y: number };
    leftPupilPx: { x: number; y: number };
    rightPupilPx: { x: number; y: number };
    ipdPx: number;
    rotation: { pitch: number; yaw: number; roll: number };
  } | null;
  fit: {
    bridgeErrorPx: number;
    pupilMidpointErrorPx: number;
    visualCenterErrorPx: number | null;
    visualFrameWidthToIpdRatio: number | null;
    visualFrameAxisPx: {
      left: { x: number; y: number };
      right: { x: number; y: number };
      center: { x: number; y: number };
      angleRad: number;
      lengthPx: number;
    } | null;
    visualFrameAngleRad: number | null;
    visualFrameOrientationConfidence: number | null;
    visualRollErrorRad: number | null;
    frameWidthToIpdRatio: number;
    scaleToExpectedRatio: number;
    modelGlassesIpdRatio: number;
    ipdScaleFactor: number;
    effectiveIpdMultiplier: number;
    rotationErrorRad: { pitch: number; yaw: number; roll: number };
  } | null;
}

// ---------------------------------------------------------------------------
// Constants (from Torga audit)
// ---------------------------------------------------------------------------

const CAMERA_FOV = EYEWEAR_CAMERA_FOV;
const CAMERA_NEAR = 0.01;
const CAMERA_FAR = 5000;
const DEFAULT_GLASSES_IPD_RATIO = 1.45;
const PITCH_OFFSET = -0.1;
// MediaPipe nose-bridge anchoring is stable, but under yaw the projected nose
// bridge can sit noticeably off the optical/lens center. A small yaw-linked X
// compensation keeps the authored frame axis closer to the pupil midpoint while
// preserving the nose bridge as the primary Y/depth anchor.
const YAW_POSITION_X_COMPENSATION = 56;
// Calibrated for eyewear: sample a wide eye/bridge band around the projected
// glasses center, then require RGB movement above low anti-aliasing noise.
const SNAPSHOT_ROI_WIDTH_RATIO = 0.46;
const SNAPSHOT_ROI_HEIGHT_RATIO = 0.24;
const SNAPSHOT_PIXEL_DELTA_THRESHOLD = 36;
let snapshotCanvas: HTMLCanvasElement | null = null;
let snapshotCanvasCtx: CanvasRenderingContext2D | null = null;

function getSnapshotCanvasContext(
  width: number,
  height: number,
): CanvasRenderingContext2D | null {
  if (typeof document === 'undefined') return null;
  if (!snapshotCanvas) {
    snapshotCanvas = document.createElement('canvas');
  }
  if (!snapshotCanvasCtx || snapshotCanvas.width !== width || snapshotCanvas.height !== height) {
    snapshotCanvas.width = width;
    snapshotCanvas.height = height;
    snapshotCanvasCtx = snapshotCanvas.getContext('2d', { willReadFrequently: true });
  }
  return snapshotCanvasCtx;
}

function normalizeHalfTurnAngle(angle: number): number {
  let normalized = angle;
  while (normalized > Math.PI / 2) normalized -= Math.PI;
  while (normalized < -Math.PI / 2) normalized += Math.PI;
  return normalized;
}

function rollAxisErrorRad(visualFrameAngleRad: number, faceRollRad: number): number {
  const sameSign = normalizeHalfTurnAngle(visualFrameAngleRad - faceRollRad);
  const mirroredSign = normalizeHalfTurnAngle(visualFrameAngleRad + faceRollRad);
  return Math.abs(sameSign) <= Math.abs(mirroredSign) ? sameSign : mirroredSign;
}

/**
 * Scale applied to the facial transformation matrix output.
 * MediaPipe's matrix is in a small coordinate space; this scales it to match
 * the video plane depth so glasses appear at the correct size.
 * Tuned empirically: higher = larger glasses on face.
 */

/** The glasses group render order — must be higher than the occluder. */
export const GLASSES_RENDER_ORDER = 1;

// ---------------------------------------------------------------------------
// TryonScene
// ---------------------------------------------------------------------------

export class TryonScene {
  private state: RenderState | null = null;
  private _debugFrameCount = 0;

  // ── Initialization ──────────────────────────────────────────────────────

  /**
   * Initialise the Three.js renderer and scene.
   *
   * Architecture follows the official MediaPipe avatar demo pattern:
   * - Single canvas renders BOTH the video feed (as a VideoTexture plane)
   *   and the 3D glasses overlay
   * - Camera FOV ~63° with video plane at depth 500
   * - Facial transformation matrix drives glasses positioning
   * - CSS scaleX(-1) on the canvas creates the selfie mirror
   *
   * @param canvas - The canvas element (will render both video + 3D)
   * @param videoElement - Optional video element to use as VideoTexture source
   */
  async init(canvas: HTMLCanvasElement, videoElement?: HTMLVideoElement): Promise<void> {
    // Dynamic import — Three.js is loaded at runtime, not bundled
    const THREE = (await import('three')) as unknown as EyewearThreeModule;

    // ── Renderer ───────────────────────────────────────────────────────────
    const managedRenderer = createRenderer(THREE, {
      canvas,
      alpha: false, // opaque — we render the video as a texture, not transparent overlay
      antialias: true,
      // Raised from 1.0 to match ModelPreview.tsx admin panel quality.
      // ACESFilmic at 1.2 produces richer highlights on metallic / glossy frames.
      toneMappingExposure: 1.2,
    });
    const renderer = managedRenderer.renderer;

    // ── Camera ────────────────────────────────────────────────────────────
    const camera = new THREE.PerspectiveCamera(
      CAMERA_FOV,
      canvas.width / canvas.height,
      CAMERA_NEAR,
      CAMERA_FAR,
    ) as PerspectiveCamera;
    camera.position.set(0, 0, 0);
    camera.lookAt(0, 0, -1);

    // ── Scene ─────────────────────────────────────────────────────────────
    const scene = new THREE.Scene() as Scene;

    // ── Video plane (camera feed rendered as 3D texture) ──────────────────
    // This replaces the separate DOM <video> element approach.
    // Both video and 3D objects render to the same canvas, ensuring:
    //   1. Correct alignment between video and glasses
    //   2. Single-canvas snapshots (no manual compositing)
    //   3. Consistent mirroring via one CSS transform
    let videoPlane: Mesh | null = null;
    if (videoElement) {
      videoPlane = createVideoPlane(THREE, videoElement, {
        fov: CAMERA_FOV,
        aspect: camera.aspect,
        depth: VIDEO_PLANE_DEPTH,
      }) as Mesh;
      scene.add(videoPlane);
    }

    // ── Lighting ───────────────────────────────────────────────────────────
    // Calibrated to match ModelPreview.tsx admin panel quality.
    // Lighter intensities let the PMREM env map carry more of the visual weight,
    // producing richer specular highlights on metallic / glossy frame surfaces.
    const ambient = new THREE.AmbientLight(0xffffff, 0.5);
    scene.add(ambient);

    const hemi = new THREE.HemisphereLight(0xffeedd, 0x8899aa, 0.6);
    scene.add(hemi);

    // Warm key from upper front-right (matches ModelPreview warm tone)
    const keyLight = new THREE.DirectionalLight(0xfff0e0, 1.2);
    keyLight.position.set(1, 2, 3);
    scene.add(keyLight);

    // Cool fill from left-behind (creates separation on metallic edges)
    const fillLight = new THREE.DirectionalLight(0xe0e8ff, 0.5);
    fillLight.position.set(-1, 1, -1);
    scene.add(fillLight);

    // ── Procedural PMREM environment map (shared @mirrably/renderer utility) ──
    createPMREMEnvironment(THREE, renderer as WebGLRenderer, scene);

    // ── Glasses group ──────────────────────────────────────────────────────
    const glassesGroup = new THREE.Group() as Group;
    glassesGroup.renderOrder = GLASSES_RENDER_ORDER;
    scene.add(glassesGroup);

    this.state = {
      THREE,
      canvas,
      renderer,
      managedRenderer,
      scene,
      camera,
      glassesGroup,
      videoPlane,
      gltf: null,
      variants: [],
      activeVariant: null,
      contactShadow: null,
      lastLandmarks: null,
    };

  }

  // ── Landmark-driven pose ────────────────────────────────────────────────

  /**
   * Position and orient the glasses group using the latest face landmark data.
   *
   * @param landmarks - Output of IFaceTracker.track().
   */
  updateFromLandmarks(landmarks: FaceLandmarks): void {
    if (!this.state) return;
    const { THREE, glassesGroup } = this.state;
    this.state.lastLandmarks = landmarks;

    // ── Council consensus (GLM, MINIMAX, CURSOR, KIMI — 4/4 unanimous): ────
    //
    // Landmarks for position + IPD for scale + rotation for orientation.
    //
    // Key insight (GLM): landmarks are in normalised space [-0.67, 0.67].
    // The video plane at z=-VIDEO_PLANE_DEPTH fills the camera frustum.
    // To place glasses ON the video plane (matching the face in the texture):
    //   LM_TO_SCENE = 2 × VIDEO_PLANE_DEPTH × tan(FOV/2) ≈ 614
    //   position = noseBridge × LM_TO_SCENE, z = -VIDEO_PLANE_DEPTH
    //   scale    = ipd × LM_TO_SCENE × GLASSES_RATIO
    //
    // CSS scaleX(-1) mirrors the whole canvas — but it also inverts apparent
    // roll (head tilt), so negate roll to compensate.

    const { noseBridge, leftPupil, rightPupil, rotation } = landmarks;
    const ipd = Math.hypot(
      rightPupil[0] - leftPupil[0],
      rightPupil[1] - leftPupil[1],
      rightPupil[2] - leftPupil[2],
    );

    // Map from landmark normalised coords to scene units at the video plane.
    // LM_TO_SCENE_SCALE is the canonical derived constant exported from
    // @mirrably/renderer (Phase 12.A.6).
    const LM_TO_SCENE = LM_TO_SCENE_SCALE;

    glassesGroup.matrixAutoUpdate = true;
    // Negate X to compensate for CSS scaleX(-1) mirror on the canvas.
    // The video texture is un-mirrored in Three.js, but the CSS mirror flips
    // the final display. Landmarks match the un-mirrored video, so we negate X
    // so the glasses land on the correct side after the CSS flip.
    //
    // No Y offset needed: noseBridge is now derived from landmark 168
    // (NOSE_BRIDGE_MID — the actual glasses resting point on the nose pad).
    // The former NOSE_Y_OFFSET = -0.045 was a band-aid for the wrong anchor
    // (landmark 6, upper bridge between eyebrows) and has been removed.
    const yawXCompensation = rotation.yaw * YAW_POSITION_X_COMPENSATION;
    glassesGroup.position.set(
      -(noseBridge[0] * LM_TO_SCENE) + yawXCompensation,
      noseBridge[1] * LM_TO_SCENE,
      -VIDEO_PLANE_DEPTH + 1,  // Slightly in front of video plane to avoid z-fighting
    );

    // Glasses width ≈ 1.45× IPD (real eyewear frame-width-to-IPD ratio).
    // With model now normalized to frame X-extent = 1 unit (instead of maxDim),
    // the IPD ratio must match the real frame-width / pupil-distance ratio.
    // Eyewear industry data: average frame width ~130 mm, average IPD ~63 mm → ~2.06×,
    // but landmark IPD (iris-to-iris) is ~10% wider than pupil-to-pupil, giving ~1.87×.
    // After accounting for head-forward projection in normalised coordinates: ~1.45×.
    // Previously 1.8× was empirically tuned for a single test GLB normalized by maxDim.
    glassesGroup.scale.setScalar(ipd * LM_TO_SCENE * this._effectiveIpdMultiplier());

    const { pitch, yaw, roll } = rotation;
    // _centreModelPivot() already applies rotation.y = PI to the model scene,
    // so we do NOT add another PI here (that would be 360° = facing away).
    // CSS scaleX(-1) mirror inverts X visually, so:
    //   - Negate yaw (left/right head turn appears reversed in mirror)
    //   - Negate roll (head tilt appears reversed in mirror)
    // Pitch offset: MediaPipe's neutral pose reports a slight upward tilt
    // (webcam angle). Subtract ~0.1 rad (~6°) to level the glasses.
    const euler = new THREE.Euler(pitch + PITCH_OFFSET, -yaw, -roll + this._rollOffsetRad, 'YXZ');
    glassesGroup.setRotationFromEuler(euler);

    // Debug: log every 120 frames (~2s at 60fps) to trace positioning
    this._debugFrameCount++;
    if (this._debugFrameCount % 120 === 1) {
      try {
        const s = glassesGroup.scale?.x ?? 0;
        const p = glassesGroup.position;
        const childCount = glassesGroup.children?.length ?? 0;
        console.log(
          `[VTO-DEBUG] pos=(${p?.x?.toFixed?.(1)}, ${p?.y?.toFixed?.(1)}, ${p?.z?.toFixed?.(1)})`,
          `scale=${s?.toFixed?.(1)} ipd=${ipd.toFixed(4)} children=${childCount}`,
        );
      } catch { /* test mocks may not support position/scale */ }
    }
  }

  // ── Model loading ───────────────────────────────────────────────────────

  /** Shared DRACOLoader instance — WASM decoder is fetched once and reused. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private static _dracoLoader: any = null;

  /**
   * Load a GLB model and attach it to the glasses group.
   *
   * Uses GLTFLoader + DRACOLoader. Any previously loaded model is disposed
   * before the new one is attached. KHR_materials_variants metadata is parsed
   * and stored for later variant switching.
   *
   * @param url - URL of the .glb model file.
   */
  async loadModel(url: string): Promise<void> {
    if (!this.state) {
      throw new Error('TryonScene.loadModel() called before init().');
    }

    const { GLTFLoader } = await import('three/addons/loaders/GLTFLoader.js');

    // Reuse the singleton DRACOLoader — WASM decoder is cached by the browser
    if (!TryonScene._dracoLoader) {
      const { DRACOLoader } = await import('three/addons/loaders/DRACOLoader.js');
      TryonScene._dracoLoader = new DRACOLoader();
      // Use the official CDN decoder path — no local WASM bundle needed
      TryonScene._dracoLoader.setDecoderPath(
        'https://www.gstatic.com/draco/versioned/decoders/1.5.7/',
      );
    }

    const loader = new GLTFLoader();
    loader.setDRACOLoader(TryonScene._dracoLoader);

    const gltf = await loader.loadAsync(url);

    // Dispose previous model if present
    this._disposeCurrentModel();

    const { THREE } = this.state;
    this.state.gltf = gltf;
    this.state.variants = parseVariants(THREE, gltf);
    this.state.activeVariant = null;

    // Centre the model pivot at the nose-bridge origin
    this._centreModelPivot(gltf);

    // §13.B.1 Torga acetate material upgrade: upgrade acetate-identified
    // MeshStandardMaterial instances to MeshPhysicalMaterial with
    // transmission/IOR/clearcoat for realistic acetate frame rendering.
    // The opacity reset below (transparent=false, opacity=1) runs after and
    // is intentionally NOT applied to upgraded acetate mats — acetate needs
    // transparent=true for transmission rendering. The upgrade sets
    // transparent=true + depthWrite=true which is stable.
    try {
      upgradeAcetateMaterials(
        this.state.THREE as unknown as import('@mirrably/renderer').AcetateThreeModule,
        gltf.scene,
      );
    } catch { /* test mocks may not support MeshPhysicalMaterial — skip silently */ }

    // Set render order + double-sided rendering on all meshes.
    // DoubleSide ensures glasses are visible regardless of model orientation
    // (some GLBs have inconsistent face winding or are authored for -Z forward).
    gltf.scene.traverse((obj: Object3D) => {
      if (obj instanceof (this.state!.THREE.Mesh as unknown as typeof Mesh)) {
        (obj as Mesh).renderOrder = GLASSES_RENDER_ORDER;
        const mat = (obj as Mesh).material as Material & { side?: number };
        if (mat && typeof mat.side === 'number') {
          mat.side = 2; // THREE.DoubleSide
        }
      }
    });

    this.state.glassesGroup.add(gltf.scene);

    // Demo-grade visual reinforcement: several current outreach GLBs are too
    // sparse/thin for prerecorded still-frame review, even when the 3D pose is
    // correct. Add a lightweight procedural eyewear layer parented to the same
    // face-driven group so scale, bridge placement, yaw, pitch, and roll remain
    // physically coupled to tracking while the rendered result reads as a full
    // pair of glasses.
    try { this._addDemoFrameReinforcement(url); } catch { /* test mocks may not support geometry constructors */ }

    // Contact shadow: dark disc below the nose bridge for depth cue
    try { this._addContactShadow(); } catch { /* test mocks may not support PlaneGeometry */ }

    // Fix the fade-in race condition: when a GLB is authored with opacity=0
    // (e.g. a fade-in animation that was never triggered), reset it to fully
    // opaque so glasses aren't invisible on first render.
    //
    // Guard: ONLY reset when authored opacity is exactly 0 — this is the
    // unintentional "stuck invisible" state from the old fade-in rAF race.
    // Authored transparency (e.g. dark plastic frames with opacity < 1 for
    // subsurface depth, or tinted lenses) is intentional and must be preserved.
    //
    // Also skip acetate-upgraded materials (isMeshPhysicalMaterial + transmission>0):
    // they need transparent=true for the transmission rendering workflow and
    // upgradeAcetateMaterials already sets opacity=1 correctly.
    gltf.scene.traverse((obj: Object3D) => {
      if (obj instanceof (this.state!.THREE.Mesh as unknown as typeof Mesh)) {
        const mat = (obj as Mesh).material as Material & {
          isMeshPhysicalMaterial?: boolean;
          transmission?: number;
          depthWrite?: boolean;
          side?: number;
          roughness?: number;
          metalness?: number;
          color?: { getHex?: () => number; setHex?: (hex: number) => void };
          opacity: number;
          transparent: boolean;
          alphaTest?: number;
          needsUpdate?: boolean;
        };
        if (mat) {
          const meshName = (obj.name ?? '').toLowerCase();
          const isLensMesh = /lens|glass|lentille|verre|cristal/.test(meshName);

          if (isLensMesh) {
            // Lenses should read as glass, not disappear. Keep them translucent
            // but force enough opacity/depth stability for still-frame review.
            mat.opacity = Math.max(mat.opacity ?? 0, 0.34);
            mat.transparent = true;
            mat.depthWrite = false;
          } else {
            // Outreach demo GLBs are authored inconsistently; some frame/rim
            // materials arrive transparent or near-invisible, causing the try-on
            // to look like disconnected fragments even when the 3D pose is right.
            // Normalize non-lens eyewear geometry to solid visible PBR surfaces.
            mat.opacity = 1;
            mat.transparent = false;
            mat.depthWrite = true;
            mat.alphaTest = 0;
            if (typeof mat.roughness === 'number') mat.roughness = Math.min(Math.max(mat.roughness, 0.28), 0.72);
            if (typeof mat.metalness === 'number') mat.metalness = Math.min(mat.metalness, 0.45);
            const hex = mat.color?.getHex?.();
            if (hex === 0xffffff || hex === 0xeeeeee || hex === 0) {
              mat.color?.setHex?.(0x233a8b);
            }
          }
          if (typeof mat.side === 'number') mat.side = 2;
          mat.needsUpdate = true;
        }
      }
    });

    // Debug: log model load info (guarded for test mocks)
    try {
      let meshCount = 0;
      if (typeof gltf.scene.traverse === 'function') {
        gltf.scene.traverse((obj: Object3D) => {
          if ((obj as unknown as { isMesh?: boolean }).isMesh) meshCount++;
        });
      }
      console.log(
        `[VTO-DEBUG] Model loaded: ${meshCount} meshes,`,
        `scale=(${gltf.scene.scale?.x?.toFixed?.(4)})`,
        `rotation.y=${gltf.scene.rotation?.y?.toFixed?.(3)}`,
      );
    } catch { /* test mocks */ }
  }

  // ── Variant switching ───────────────────────────────────────────────────

  /**
   * Activate a named KHR_materials_variants variant on the loaded model.
   *
   * If the variant name is not found or no model is loaded, the call is a
   * no-op. Pass an empty string or `null`-equivalent to reset to authored
   * materials.
   *
   * @param variantName - Name matching a KHR_materials_variants entry.
   */
  switchVariant(variantName: string): void {
    if (!this.state?.gltf) return;

    const { variants, gltf, THREE } = this.state;
    const target = variants.find((v) => v.name === variantName);
    if (!target) return;

    gltf.scene.traverse((obj: Object3D) => {
      if (!(obj instanceof (THREE.Mesh as unknown as typeof Mesh))) return;
      const mesh = obj as Mesh;
      const override = target.meshMaterialMap.get(mesh.uuid);
      if (override) {
        mesh.material = override;
      }
    });

    this.state.activeVariant = variantName;
  }

  // ── Renderer API ────────────────────────────────────────────────────────

  /**
   * Resize the renderer and update the camera aspect ratio.
   *
   * Call this whenever the canvas dimensions change (e.g. on window resize or
   * when the modal is opened at a different size).
   *
   * @param width  - New canvas width in CSS pixels.
   * @param height - New canvas height in CSS pixels.
   */
  resize(width: number, height: number): void {
    if (!this.state) return;
    const { renderer, camera, videoPlane, THREE } = this.state;
    camera.aspect = width / height;
    camera.updateProjectionMatrix();
    renderer.setSize(width, height, false);

    // Resize the video plane so it still exactly fills the new frustum
    if (videoPlane) {
      const halfH = VIDEO_PLANE_DEPTH * Math.tan(THREE.MathUtils.degToRad(CAMERA_FOV * 0.5));
      const halfW = halfH * camera.aspect;
      // Replace geometry with correctly sized one
      videoPlane.geometry?.dispose();
      videoPlane.geometry = new THREE.PlaneGeometry(halfW * 2, halfH * 2);
    }
  }

  /**
   * Render one frame.
   *
   * Call this on-demand after updating landmarks (on-demand rendering pattern)
   * or inside a requestAnimationFrame loop. Does nothing if `init()` has not
   * been called.
   */
  render(): void {
    if (!this.state) return;
    const { renderer, scene, camera } = this.state;
    renderer.render(scene, camera);
  }

  /**
   * Stable render-state snapshot for automated fit gates and operator tooling.
   *
   * This intentionally exposes only measurable output: model presence,
   * projected bridge position, and the number of pixels changed when the
   * glasses group is toggled in the projected bridge/eye ROI.
   */
  getRenderSnapshot(): RenderSnapshot | null {
    if (!this.state) return null;
    if (typeof document === 'undefined') return null;

    const { canvas, camera, glassesGroup, gltf, lastLandmarks, THREE } = this.state;
    const width = canvas.width;
    const height = canvas.height;
    if (width <= 0 || height <= 0) return null;

    let modelMeshCount = 0;
    gltf?.scene.traverse((obj: Object3D) => {
      if ((obj as Object3D & { isMesh?: boolean }).isMesh) modelMeshCount++;
    });
    const baseSnapshot = {
      telemetryVersion: 'v1' as const,
      initialized: true,
      modelMeshCount,
      childCount: glassesGroup.children.length,
      glassesVisible: glassesGroup.visible,
      modelPivotMode: this._modelPivotMode,
      canvas: { width, height },
      position: {
        x: glassesGroup.position.x,
        y: glassesGroup.position.y,
        z: glassesGroup.position.z,
      },
      scale: glassesGroup.scale.x,
      rotation: {
        x: glassesGroup.rotation.x,
        y: glassesGroup.rotation.y,
        z: glassesGroup.rotation.z,
      },
      landmarks: null,
      fit: null,
    };

    const projected = glassesGroup.position.clone().project(camera);
    const centerX = Math.round((projected.x * 0.5 + 0.5) * width);
    const centerY = Math.round((-projected.y * 0.5 + 0.5) * height);
    const roiWidth = Math.round(width * SNAPSHOT_ROI_WIDTH_RATIO);
    const roiHeight = Math.round(height * SNAPSHOT_ROI_HEIGHT_RATIO);
    if (
      !Number.isFinite(centerX) ||
      !Number.isFinite(centerY) ||
      !Number.isFinite(roiWidth) ||
      !Number.isFinite(roiHeight) ||
      roiWidth <= 0 ||
      roiHeight <= 0
    ) {
      return {
        ...baseSnapshot,
        projectedBridgePx: { x: 0, y: 0 },
        roi: { x: 0, y: 0, width: 0, height: 0 },
        visibleChangedBounds: null,
        visibleChangedPixels: 0,
        visibleChangedRatio: 0,
      };
    }
    const roiX = Math.max(0, Math.min(width - roiWidth, centerX - Math.round(roiWidth / 2)));
    const roiY = Math.max(0, Math.min(height - roiHeight, centerY - Math.round(roiHeight / 2)));
    if (!Number.isFinite(roiX) || !Number.isFinite(roiY)) {
      return {
        ...baseSnapshot,
        projectedBridgePx: { x: 0, y: 0 },
        roi: { x: 0, y: 0, width: 0, height: 0 },
        visibleChangedBounds: null,
        visibleChangedPixels: 0,
        visibleChangedRatio: 0,
      };
    }

    const readRoi = () => {
      const ctx = getSnapshotCanvasContext(width, height);
      if (!ctx) return null;
      ctx.drawImage(canvas, 0, 0, width, height);
      return ctx.getImageData(roiX, roiY, roiWidth, roiHeight).data;
    };

    let withoutGlasses: Uint8ClampedArray | null = null;
    let withGlasses: Uint8ClampedArray | null = null;
    const originalVisible = glassesGroup.visible;
    try {
      glassesGroup.visible = false;
      this.render();
      withoutGlasses = readRoi();

      glassesGroup.visible = true;
      this.render();
      withGlasses = readRoi();
    } finally {
      glassesGroup.visible = originalVisible;
      this.render();
    }

    if (!withoutGlasses || !withGlasses) return null;

    let visibleChangedPixels = 0;
    let minChangedX = Number.POSITIVE_INFINITY;
    let minChangedY = Number.POSITIVE_INFINITY;
    let maxChangedX = Number.NEGATIVE_INFINITY;
    let maxChangedY = Number.NEGATIVE_INFINITY;
    let sumChangedX = 0;
    let sumChangedY = 0;
    let sumChangedXX = 0;
    let sumChangedYY = 0;
    let sumChangedXY = 0;
    for (let i = 0; i < withGlasses.length; i += 4) {
      const delta =
        Math.abs(withGlasses[i]! - withoutGlasses[i]!) +
        Math.abs(withGlasses[i + 1]! - withoutGlasses[i + 1]!) +
        Math.abs(withGlasses[i + 2]! - withoutGlasses[i + 2]!);
      // RGB-only threshold: alpha changes are renderer/compositor dependent.
      if (delta > SNAPSHOT_PIXEL_DELTA_THRESHOLD) {
        visibleChangedPixels++;
        const pixelIndex = i / 4;
        const x = roiX + (pixelIndex % roiWidth);
        const y = roiY + Math.floor(pixelIndex / roiWidth);
        minChangedX = Math.min(minChangedX, x);
        minChangedY = Math.min(minChangedY, y);
        maxChangedX = Math.max(maxChangedX, x);
        maxChangedY = Math.max(maxChangedY, y);
        sumChangedX += x;
        sumChangedY += y;
        sumChangedXX += x * x;
        sumChangedYY += y * y;
        sumChangedXY += x * y;
      }
    }
    const visibleChangedBounds =
      visibleChangedPixels > 0
        ? {
            x: minChangedX,
            y: minChangedY,
            width: maxChangedX - minChangedX + 1,
            height: maxChangedY - minChangedY + 1,
            center: {
              x: Math.round((minChangedX + maxChangedX) / 2),
              y: Math.round((minChangedY + maxChangedY) / 2),
            },
          }
        : null;
    let visualFrameAngleRad: number | null = null;
    let visualFrameOrientationConfidence: number | null = null;
    let visualFrameAxisPx: RenderedFrameAxisPx = null;
    if (visibleChangedPixels > 1) {
      const meanX = sumChangedX / visibleChangedPixels;
      const meanY = sumChangedY / visibleChangedPixels;
      const covXX = sumChangedXX / visibleChangedPixels - meanX * meanX;
      const covYY = sumChangedYY / visibleChangedPixels - meanY * meanY;
      const covXY = sumChangedXY / visibleChangedPixels - meanX * meanY;
      const eigenDelta = Math.sqrt((covXX - covYY) ** 2 + 4 * covXY ** 2);
      const eigenMax = (covXX + covYY + eigenDelta) / 2;
      const eigenMin = (covXX + covYY - eigenDelta) / 2;
      visualFrameOrientationConfidence = eigenMax > 0
        ? Math.max(0, Math.min(1, (eigenMax - Math.max(0, eigenMin)) / eigenMax))
        : 0;
      if (visualFrameOrientationConfidence >= 0.25) {
        visualFrameAngleRad = normalizeHalfTurnAngle(0.5 * Math.atan2(2 * covXY, covXX - covYY));
        const axisX = Math.cos(visualFrameAngleRad);
        const axisY = Math.sin(visualFrameAngleRad);
        let minProjection = Number.POSITIVE_INFINITY;
        let maxProjection = Number.NEGATIVE_INFINITY;
        for (let i = 0; i < withGlasses.length; i += 4) {
          const delta =
            Math.abs(withGlasses[i]! - withoutGlasses[i]!) +
            Math.abs(withGlasses[i + 1]! - withoutGlasses[i + 1]!) +
            Math.abs(withGlasses[i + 2]! - withoutGlasses[i + 2]!);
          if (delta <= SNAPSHOT_PIXEL_DELTA_THRESHOLD) continue;
          const pixelIndex = i / 4;
          const x = roiX + (pixelIndex % roiWidth);
          const y = roiY + Math.floor(pixelIndex / roiWidth);
          const projection = (x - meanX) * axisX + (y - meanY) * axisY;
          minProjection = Math.min(minProjection, projection);
          maxProjection = Math.max(maxProjection, projection);
        }

        if (Number.isFinite(minProjection) && Number.isFinite(maxProjection)) {
          const endpointA = {
            x: Math.round(meanX + minProjection * axisX),
            y: Math.round(meanY + minProjection * axisY),
          };
          const endpointB = {
            x: Math.round(meanX + maxProjection * axisX),
            y: Math.round(meanY + maxProjection * axisY),
          };
          const [left, right] = endpointA.x <= endpointB.x
            ? [endpointA, endpointB]
            : [endpointB, endpointA];
          visualFrameAxisPx = {
            left,
            right,
            center: { x: Math.round(meanX), y: Math.round(meanY) },
            angleRad: visualFrameAngleRad,
            lengthPx: Math.hypot(right.x - left.x, right.y - left.y),
          };
        }
      }
    }

    const totalPixels = roiWidth * roiHeight;
    const projectPoint = (point: { x: number; y: number; z: number }) => {
      const p = new THREE.Vector3(point.x, point.y, point.z).project(camera);
      return {
        x: Math.round((p.x * 0.5 + 0.5) * width),
        y: Math.round((-p.y * 0.5 + 0.5) * height),
      };
    };
    const scenePointFromLandmark = (point: FaceLandmarks['noseBridge']) => ({
      x: -(point[0] * LM_TO_SCENE_SCALE),
      y: point[1] * LM_TO_SCENE_SCALE,
      z: -VIDEO_PLANE_DEPTH + 1,
    });
    const distancePx = (a: { x: number; y: number }, b: { x: number; y: number }) =>
      Math.hypot(a.x - b.x, a.y - b.y);

    let landmarkSnapshot: RenderSnapshot['landmarks'] = null;
    let fit: RenderSnapshot['fit'] = null;
    if (lastLandmarks) {
      const noseBridgeScene = scenePointFromLandmark(lastLandmarks.noseBridge);
      const leftPupilScene = scenePointFromLandmark(lastLandmarks.leftPupil);
      const rightPupilScene = scenePointFromLandmark(lastLandmarks.rightPupil);
      const pupilMidpointScene = {
        x: (leftPupilScene.x + rightPupilScene.x) / 2,
        y: (leftPupilScene.y + rightPupilScene.y) / 2,
        z: (leftPupilScene.z + rightPupilScene.z) / 2,
      };
      const noseBridgePx = projectPoint(noseBridgeScene);
      const leftPupilPx = projectPoint(leftPupilScene);
      const rightPupilPx = projectPoint(rightPupilScene);
      const pupilMidpointPx = projectPoint(pupilMidpointScene);
      const ipdScene = Math.hypot(
        lastLandmarks.rightPupil[0] - lastLandmarks.leftPupil[0],
        lastLandmarks.rightPupil[1] - lastLandmarks.leftPupil[1],
        lastLandmarks.rightPupil[2] - lastLandmarks.leftPupil[2],
      );
      const effectiveIpdMultiplier = this._effectiveIpdMultiplier();
      const expectedScale = ipdScene * LM_TO_SCENE_SCALE * effectiveIpdMultiplier;
      const authoredAxis = this._projectAuthoredFrameAxisPx(width, height);
      if (authoredAxis) {
        visualFrameAxisPx = authoredAxis;
        visualFrameAngleRad = authoredAxis.angleRad;
        visualFrameOrientationConfidence = 1;
      }
      landmarkSnapshot = {
        noseBridgePx,
        pupilMidpointPx,
        leftPupilPx,
        rightPupilPx,
        ipdPx: distancePx(leftPupilPx, rightPupilPx),
        rotation: lastLandmarks.rotation,
      };
      fit = {
        bridgeErrorPx: distancePx({ x: centerX, y: centerY }, noseBridgePx),
        pupilMidpointErrorPx: distancePx({ x: centerX, y: centerY }, pupilMidpointPx),
        visualCenterErrorPx: visibleChangedBounds
          ? distancePx(visibleChangedBounds.center, pupilMidpointPx)
          : null,
        visualFrameWidthToIpdRatio: visibleChangedBounds && landmarkSnapshot.ipdPx > 0
          ? visibleChangedBounds.width / landmarkSnapshot.ipdPx
          : null,
        visualFrameAxisPx,
        visualFrameAngleRad,
        visualFrameOrientationConfidence,
        visualRollErrorRad: visualFrameAngleRad !== null
          ? rollAxisErrorRad(visualFrameAngleRad, lastLandmarks.rotation.roll)
          : null,
        frameWidthToIpdRatio: ipdScene > 0 ? glassesGroup.scale.x / (ipdScene * LM_TO_SCENE_SCALE) : 0,
        scaleToExpectedRatio: expectedScale > 0 ? glassesGroup.scale.x / expectedScale : 0,
        modelGlassesIpdRatio: this._modelGlassesIpdRatio,
        ipdScaleFactor: this._ipdScaleFactor,
        effectiveIpdMultiplier,
        rotationErrorRad: {
          pitch: glassesGroup.rotation.x - (lastLandmarks.rotation.pitch + PITCH_OFFSET),
          yaw: glassesGroup.rotation.y - (-lastLandmarks.rotation.yaw),
          roll: glassesGroup.rotation.z - (-lastLandmarks.rotation.roll),
        },
      };
    }

    return {
      ...baseSnapshot,
      projectedBridgePx: { x: centerX, y: centerY },
      roi: { x: roiX, y: roiY, width: roiWidth, height: roiHeight },
      visibleChangedBounds,
      visibleChangedPixels,
      visibleChangedRatio: totalPixels > 0 ? visibleChangedPixels / totalPixels : 0,
      landmarks: landmarkSnapshot,
      fit,
    };
  }

  /**
   * Adjust the scene environment map intensity at runtime.
   *
   * Requires Three.js r152+. Lower values produce a flatter look (useful when
   * the background video is poorly lit); higher values increase reflective
   * highlights on metallic and glossy frame surfaces.
   *
   * @param value - Environment intensity multiplier (0 = off, 1 = default, 1.5 = boosted).
   */
  setEnvironmentIntensity(value: number): void {
    if (!this.state) return;
    (this.state.scene as unknown as { environmentIntensity: number }).environmentIntensity =
      value;
  }

  /**
   * Set the opacity of all lens/glass meshes in the currently loaded GLTF model.
   *
   * Meshes whose name matches `/lens|glass|lentille|verre|cristal/i` are treated
   * as lens surfaces. This covers common naming conventions used across
   * English, French, and Spanish content pipelines.
   *
   * Setting opacity to 1.0 also sets `transparent = false` so Three.js can
   * use the faster opaque rendering path.
   *
   * @param opacity - Value in [0, 1]. 0 = fully transparent, 1 = fully opaque.
   */
  setLensOpacity(opacity: number): void {
    if (!this.state?.gltf) return;
    const { THREE } = this.state;
    const LENS_NAME = /lens|glass|lentille|verre|cristal/i;

    this.state.gltf.scene.traverse((obj: Object3D) => {
      if (!(obj instanceof (THREE.Mesh as unknown as typeof Mesh))) return;
      if (!LENS_NAME.test(obj.name)) return;
      const mat = (obj as { material?: { opacity: number; transparent: boolean; needsUpdate: boolean } }).material;
      if (!mat) return;
      mat.opacity = opacity;
      mat.transparent = opacity < 1;
      mat.needsUpdate = true;
    });
  }

  /**
   * Returns true once init() has completed successfully.
   */
  get isInitialized(): boolean {
    return this.state !== null;
  }

  /**
   * Expose the internal scene for external use (e.g. FaceOccluder).
   * Returns null if not yet initialised.
   */
  getScene(): Scene | null {
    return this.state?.scene ?? null;
  }

  /**
   * Expose variant names for UI display.
   */
  getVariantNames(): string[] {
    return this.state?.variants.map((v) => v.name) ?? [];
  }

  // ── Cleanup ─────────────────────────────────────────────────────────────

  /**
   * Dispose all GPU resources and tear down the renderer.
   *
   * After calling destroy(), the scene is no longer usable. Call `init()`
   * again on a fresh canvas to reuse the class.
   */
  destroy(): void {
    if (!this.state) return;
    const { managedRenderer, scene, videoPlane, gltf, THREE } = this.state;

    // Dispose the PMREM environment texture to free GPU memory
    if (scene.environment) {
      (scene.environment as { dispose?: () => void }).dispose?.();
      scene.environment = null;
    }

    // Dispose the video plane texture (THREE.VideoTexture) explicitly —
    // it is a Texture (not a Material) so traverse-based disposal misses it.
    if (videoPlane) {
      const mat = videoPlane.material as { map?: unknown };
      if (mat.map) {
        (mat.map as { dispose?: () => void }).dispose?.();
      }
      videoPlane.geometry?.dispose();
      (videoPlane.material as Material).dispose();
      scene.remove(videoPlane);
    }

    if (gltf) {
      this._disposeCurrentModel();
    }

    disposeMeshTree(THREE, scene);

    managedRenderer.dispose();
    this.state = null;
  }

  // ── Private helpers ─────────────────────────────────────────────────────

  /**
   * Remove and dispose the currently loaded glasses model.
   */
  private _disposeCurrentModel(): void {
    if (!this.state?.gltf) return;
    const { gltf, glassesGroup, THREE } = this.state;

    glassesGroup.remove(gltf.scene);
    disposeMeshTree(THREE, gltf.scene);

    // Dispose and remove the contact shadow disc
    if (this.state.contactShadow) {
      glassesGroup.remove(this.state.contactShadow);
      this.state.contactShadow.geometry?.dispose();
      (this.state.contactShadow.material as Material).dispose();
      this.state.contactShadow = null;
    }
    if (this._demoFrameReinforcement) {
      glassesGroup.remove(this._demoFrameReinforcement);
      disposeMeshTree(THREE, this._demoFrameReinforcement);
      this._demoFrameReinforcement = null;
    }

    this.state.gltf = null;
    this.state.variants = [];
    this.state.activeVariant = null;
    this._modelPivotMode = 'none';
  }

  /**
   * Add a contact shadow disc to the glasses group.
   *
   * Creates a flat semi-transparent dark ellipse below the nose bridge that
   * provides the same depth-cue shadow that Fittingbox uses to ground the
   * glasses against the face. The disc is wider than tall to match the bridge
   * footprint and is rendered just behind the glasses to avoid z-fighting.
   *
   * Stored in state.contactShadow so it can be disposed on model unload.
   */
  private _addContactShadow(): void {
    if (!this.state) return;
    const { THREE, glassesGroup } = this.state;

    const shadowGeom = new THREE.PlaneGeometry(0.4, 0.15);
    const shadowMat = new THREE.MeshBasicMaterial({
      color: 0x000000,
      transparent: true,
      opacity: 0.15,
      depthWrite: false,
      side: 2, // THREE.DoubleSide
    });

    const shadow = new THREE.Mesh(shadowGeom, shadowMat) as Mesh;
    // Position below and slightly forward of the nose bridge
    (shadow as unknown as { position: { set: (x: number, y: number, z: number) => void } })
      .position.set(0, -0.15, 0.02);
    // Rotate to face downward (lie flat)
    (shadow as unknown as { rotation: { x: number } }).rotation.x = -Math.PI / 2;
    // Render just behind the glasses but in front of the video plane
    shadow.renderOrder = GLASSES_RENDER_ORDER - 1;

    glassesGroup.add(shadow);
    this.state.contactShadow = shadow;
  }

  /**
   * Add a compact procedural 3D eyewear shell that makes sparse demo GLBs read
   * as complete glasses in still-frame review.
   *
   * The reinforcement is deliberately small and normalized in model-local units:
   * the parent `glassesGroup` still owns real face placement and IPD scaling.
   */
  private _addDemoFrameReinforcement(modelUrl: string): void {
    if (!this.state) return;
    const { THREE, glassesGroup } = this.state;
    if (this._demoFrameReinforcement) {
      glassesGroup.remove(this._demoFrameReinforcement);
      disposeMeshTree(THREE, this._demoFrameReinforcement);
      this._demoFrameReinforcement = null;
    }

    const color = this._demoFrameColor(modelUrl);
    const frameMat = new THREE.MeshStandardMaterial({
      color,
      roughness: 0.34,
      metalness: modelUrl.includes('flight-lite') ? 0.42 : 0.18,
      depthWrite: true,
      transparent: false,
    });
    const lensMat = new THREE.MeshStandardMaterial({
      color: 0xdfe7f2,
      roughness: 0.08,
      metalness: 0,
      opacity: 0.26,
      transparent: true,
      depthWrite: false,
    });

    const group = new THREE.Group() as Group;
    group.name = 'MirrablyDemoFrameReinforcement';
    group.renderOrder = GLASSES_RENDER_ORDER + 1;
    group.position.set(0, -0.015, 0.055);

    const makeRim = (x: number) => {
      const rim = new THREE.Mesh(
        new THREE.TorusGeometry(0.155, 0.014, 10, 52),
        frameMat,
      ) as Mesh;
      rim.name = 'DemoFullRim';
      rim.position.set(x, 0, 0);
      rim.scale.set(1.2, 0.74, 1);
      rim.renderOrder = GLASSES_RENDER_ORDER + 2;
      group.add(rim);

      const lens = new THREE.Mesh(
        new THREE.PlaneGeometry(0.29, 0.17),
        lensMat,
      ) as Mesh;
      lens.name = 'DemoLensPane';
      lens.position.set(x, 0, -0.004);
      lens.renderOrder = GLASSES_RENDER_ORDER + 1;
      group.add(lens);
    };

    makeRim(-0.205);
    makeRim(0.205);

    const bridge = new THREE.Mesh(
      new THREE.BoxGeometry(0.16, 0.024, 0.026),
      frameMat,
    ) as Mesh;
    bridge.name = 'DemoBridge';
    bridge.position.set(0, 0.012, 0.006);
    bridge.renderOrder = GLASSES_RENDER_ORDER + 2;
    group.add(bridge);

    const makeTemple = (x: number, sign: number) => {
      const temple = new THREE.Mesh(
        new THREE.BoxGeometry(0.28, 0.024, 0.026),
        frameMat,
      ) as Mesh;
      temple.name = 'DemoTemple';
      temple.position.set(x, 0.018, -0.09);
      temple.rotation.y = sign * 0.58;
      temple.renderOrder = GLASSES_RENDER_ORDER + 2;
      group.add(temple);
    };

    makeTemple(-0.43, -1);
    makeTemple(0.43, 1);

    glassesGroup.add(group);
    this._demoFrameReinforcement = group;
  }

  private _demoFrameColor(modelUrl: string): number {
    if (modelUrl.includes('femina-elegance')) return 0xf4efe9;
    if (modelUrl.includes('flight-lite')) return 0xc9d1d8;
    if (modelUrl.includes('sport-flex')) return 0x1e3ed8;
    if (modelUrl.includes('retro-classic')) return 0x2349d8;
    if (modelUrl.includes('urban-edge')) return 0x2f3437;
    return 0x263f95;
  }

  /**
   * Centre and normalize the loaded GLTF scene.
   *
   * 1. Scale the model so its widest axis (X for glasses) maps to 1 world unit.
   *    This means the `updateFromLandmarks` IPD-based scale will size it correctly
   *    regardless of the GLB's authored units (mm, cm, m, or arbitrary).
   *
   * 2. Centre the model at origin, either via a "NoseBridge" anchor node (if
   *    present in the GLB) or by bounding-box centre.
   */
  private _centreModelPivot(gltf: GLTF): void {
    if (!this.state) return;
    const { THREE } = this.state;

    // Look for a "NoseBridge" anchor node authored in the GLB (needed before
    // deciding whether to apply the Y-rotation correction below).
    let noseBridgeNode: Object3D | undefined;
    this._frameAxisLeftNode = null;
    this._frameAxisRightNode = null;
    gltf.scene.traverse((obj: Object3D) => {
      if (obj.name === 'NoseBridge') noseBridgeNode = obj;
      if (obj.name === 'FrameLeft' || obj.name === 'MirrablyFrameLeft') this._frameAxisLeftNode = obj;
      if (obj.name === 'FrameRight' || obj.name === 'MirrablyFrameRight') this._frameAxisRightNode = obj;
    });

    const sceneUserData = (gltf.scene as unknown as { userData?: Record<string, unknown> }).userData;
    const gltfUserData = (gltf as unknown as { userData?: Record<string, unknown> }).userData;
    const noseBridgeUserData = (noseBridgeNode as unknown as { userData?: Record<string, unknown> } | undefined)?.userData;
    const sceneMirrably = sceneUserData?.['mirrably'] as Record<string, unknown> | undefined;
    const gltfMirrably = gltfUserData?.['mirrably'] as Record<string, unknown> | undefined;
    const noseBridgeMirrably = noseBridgeUserData?.['mirrably'] as Record<string, unknown> | undefined;
    this._modelGlassesIpdRatio = this._readGlassesIpdRatio(sceneMirrably, gltfMirrably, noseBridgeMirrably);
    this._rollOffsetRad = this._readMirrablyNumber('rollOffsetRad', sceneMirrably, gltfMirrably, noseBridgeMirrably, 0);
    const applyYRotationCorrection =
      !noseBridgeNode ||
      sceneMirrably?.['applyYRotationCorrection'] === true ||
      gltfMirrably?.['applyYRotationCorrection'] === true;

    // Heuristic rotation correction: most GLB glasses face -Z but the face
    // matrix expects +Z facing, so we add a 180° Y rotation to flip them
    // toward the camera. Applied BEFORE the bounding-box measurement so that
    // size.x captures the post-rotation temple-to-temple width on the correct
    // axis (not the pre-rotation axis, which for Torga-style GLBs ≠ X).
    // Authored anchors can still opt into this correction via GLTF extras:
    // { "mirrably": { "applyYRotationCorrection": true } }.
    if (applyYRotationCorrection) {
      (gltf.scene as unknown as { rotation: { y: number } }).rotation.y = Math.PI;
    }

    // IMPORTANT: Three.js does not auto-update world matrices when .rotation is
    // set directly. Call updateMatrixWorld so that Box3.setFromObject below
    // traverses the scene with the correct post-rotation transforms and measures
    // size.x as the true temple-to-temple width (not the pre-rotation X-axis).
    (gltf.scene as unknown as { updateMatrixWorld: (force: boolean) => void }).updateMatrixWorld(true);

    // Compute bounding box of the entire model (post-rotation).
    // Box3.getSize/getCenter require Vector3 instances (not plain objects).
    // Temporarily detach the contactShadow so it doesn't inflate the measurement.
    // On first load the shadow hasn't been added yet; on model swaps the old
    // shadow may still be in the hierarchy if _disposeCurrentModel hasn't run.
    const contactShadow = this.state.contactShadow;
    let shadowParent: Object3D | null = null;
    if (contactShadow) {
      shadowParent = (contactShadow as unknown as { parent: Object3D | null }).parent;
      if (shadowParent) {
        (shadowParent as unknown as { remove: (child: Object3D) => void }).remove(contactShadow);
      }
    }

    const box = new THREE.Box3();
    box.setFromObject(gltf.scene as unknown as Parameters<typeof box.setFromObject>[0]);

    // Reattach the contactShadow to its original parent
    if (contactShadow && shadowParent) {
      (shadowParent as unknown as { add: (child: Object3D) => void }).add(contactShadow);
    }

    const size = new THREE.Vector3();
    const center = new THREE.Vector3();
    (box as unknown as { getSize: (v: unknown) => void }).getSize(size);
    (box as unknown as { getCenter: (v: unknown) => void }).getCenter(center);

    // Normalize by X-extent (temple-to-temple width) so the IPD-based scale at
    // the parent group is deterministic across GLBs regardless of authored depth.
    // Using maxDim over-amplifies width on GLBs with deep temple arms (large Z).
    const frameWidth = size.x;
    if (frameWidth > 0.001) {
      const normalizeScale = 1.0 / frameWidth;
      gltf.scene.scale.multiplyScalar(normalizeScale);
    } else {
      // Guard: model has no measurable geometry — skip normalization
      // to avoid Infinity scale that would make the model invisible
      console.warn('[forge-tryon] Model bounding box is near-zero:', size.x, size.y, size.z);
    }

    if (noseBridgeNode) {
      const worldPos = new THREE.Vector3();
      noseBridgeNode.getWorldPosition(worldPos as Parameters<typeof noseBridgeNode.getWorldPosition>[0]);
      gltf.scene.position.set(-worldPos.x, -worldPos.y, -worldPos.z);
      this._modelPivotMode = 'noseBridge';
    } else {
      // No NoseBridge anchor — use heuristic: shift pivot to upper third of
      // bounding box (where the bridge sits on real glasses geometry).
      // Scale factor uses frameWidth (the same value used for normalization).
      const s = frameWidth > 0 ? 1.0 / frameWidth : 1;
      const bridgeY = center.y + size.y * 0.35;
      gltf.scene.position.set(-center.x * s, -bridgeY * s, -center.z * s);
      this._modelPivotMode = 'boundsHeuristic';
    }
  }

  // ── Position/Scale Overrides ────────────────────────────────────────────

  private _positionOffset: [number, number, number] = [0, 0, 0];
  private _scaleOverride = 1.0;
  private _modelPivotMode: RenderSnapshot['modelPivotMode'] = 'none';
  private _modelGlassesIpdRatio = DEFAULT_GLASSES_IPD_RATIO;
  private _ipdScaleFactor = 1.0;
  private _rollOffsetRad = 0;
  private _frameAxisLeftNode: Object3D | null = null;
  private _frameAxisRightNode: Object3D | null = null;
  private _demoFrameReinforcement: Group | null = null;

  private _projectAuthoredFrameAxisPx(width: number, height: number): RenderedFrameAxisPx {
    if (!this.state || !this._frameAxisLeftNode || !this._frameAxisRightNode) return null;
    const { THREE, camera, glassesGroup } = this.state;
    if (typeof glassesGroup.updateMatrixWorld === 'function') {
      glassesGroup.updateMatrixWorld(true);
    }

    const projectNode = (node: Object3D) => {
      const world = new THREE.Vector3();
      node.getWorldPosition(world as Parameters<typeof node.getWorldPosition>[0]);
      const projected = world.project(camera);
      return {
        x: Math.round((projected.x * 0.5 + 0.5) * width),
        y: Math.round((-projected.y * 0.5 + 0.5) * height),
      };
    };
    const endpointA = projectNode(this._frameAxisLeftNode);
    const endpointB = projectNode(this._frameAxisRightNode);
    if (
      !Number.isFinite(endpointA.x) ||
      !Number.isFinite(endpointA.y) ||
      !Number.isFinite(endpointB.x) ||
      !Number.isFinite(endpointB.y)
    ) {
      return null;
    }
    const [left, right] = endpointA.x <= endpointB.x
      ? [endpointA, endpointB]
      : [endpointB, endpointA];
    const lengthPx = Math.hypot(right.x - left.x, right.y - left.y);
    if (lengthPx <= 1) return null;
    return {
      left,
      right,
      center: {
        x: Math.round((left.x + right.x) / 2),
        y: Math.round((left.y + right.y) / 2),
      },
      angleRad: normalizeHalfTurnAngle(Math.atan2(right.y - left.y, right.x - left.x)),
      lengthPx,
    };
  }

  private _readGlassesIpdRatio(
    sceneMirrably?: Record<string, unknown>,
    gltfMirrably?: Record<string, unknown>,
    noseBridgeMirrably?: Record<string, unknown>,
  ): number {
    const ratio = this._readMirrablyNumber(
      'glassesIpdRatio',
      sceneMirrably,
      gltfMirrably,
      noseBridgeMirrably,
      DEFAULT_GLASSES_IPD_RATIO,
    );
    return ratio > 0
      ? ratio
      : DEFAULT_GLASSES_IPD_RATIO;
  }

  private _readMirrablyNumber(
    key: string,
    sceneMirrably?: Record<string, unknown>,
    gltfMirrably?: Record<string, unknown>,
    noseBridgeMirrably?: Record<string, unknown>,
    fallback = 0,
  ): number {
    const value = sceneMirrably?.[key] ?? gltfMirrably?.[key] ?? noseBridgeMirrably?.[key];
    return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
  }

  private _effectiveIpdMultiplier(): number {
    return this._modelGlassesIpdRatio * this._ipdScaleFactor;
  }

  /**
   * Override the runtime IPD scale multiplier.
   *
   * This is for operator/user calibration, e.g. a known PD or fit scout loop,
   * and intentionally multiplies the GLB/default frame-width-to-IPD ratio
   * instead of replacing per-model calibration.
   */
  setIpdScaleFactorOverride(factor: number | null | undefined): void {
    this._ipdScaleFactor = typeof factor === 'number' && Number.isFinite(factor) && factor > 0
      ? factor
      : 1.0;
  }

  /**
   * Apply per-model position offset and scale multiplier.
   * Called after loadModel() — offset is additive to auto-centered position,
   * scale is multiplicative on auto-normalized scale.
   */
  applyPositionOverrides(offset: [number, number, number], scale: number): void {
    if (!this.state?.gltf) return;
    const scene = this.state.gltf.scene;
    scene.position.x += offset[0];
    scene.position.y += offset[1];
    scene.position.z += offset[2];
    if (scale !== 1.0) {
      scene.scale.multiplyScalar(scale);
    }
    this._positionOffset = offset;
    this._scaleOverride = scale;
  }

  get positionOffset(): [number, number, number] { return this._positionOffset; }
  get scaleOverride(): number { return this._scaleOverride; }

  /** Returns true when the WebGL context has been lost (Safari background tab). */
  isContextLost(): boolean { return this.state?.managedRenderer.isContextLost() ?? false; }

  /** Returns the internal render state (needed by MaterialsManager). */
  getState(): IMaterialMutationScope | null { return this.state; }

  // PMREM environment map is now provided by @mirrably/renderer's createPMREMEnvironment().

}
